import { ClickAwayListener, Popper } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import {
  ModalityVirtualVolume,
  PriceModel,
} from '../../../../shared/api/hscan/hospital';
import { byteToString } from '../../../../shared/utils/dataFormat';
import { calculateMediaUnitCapacity } from '../../../../shared/utils/price';
import infoIcon from '../../assets/icons/icon_info.svg';
import { Spacer } from '../../common/components/Spacer';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, useWindowSize } from '../../hooks/windowSizeHook';
import { krwCurrencyFormatter as formatter } from '../../utils';

const VolumeInfoContainer = styled.div`
  position: relative;
  margin: ${p => (p.theme.isMobile ? '0 0 0 auto' : '0 auto')};
  width: fit-content;
`;

const Message = styled.span`
  vertical-align: middle;
`;

const VolumeInfoIconModalContainer = styled.div`
  display: contents;
`;

const VolumeInfoIcon = styled.button`
  display: inline-block;
  margin-left: 4px;
  width: 24px;
  height: 24px;
  border: none;
  vertical-align: middle;
  background-image: url(${infoIcon});
  background-color: transparent;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  color: #a3a6ab;
`;

const VolumeInfoModal = styled(Popper)`
  max-width: calc(100vw - 40px);
  box-sizing: border-box;
  padding: 12px 32px;
  background: white;
  box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.2);

  ${VolumeInfoIcon}:focus + & {
    display: block;
  }
`;

const VolumeInfoHeading = styled.div`
  text-align: justify;
  font-weight: bold;
  color: black;
  font-size: 16px;
`;

const TableWrapper = styled.div`
  margin: 0 auto;
  margin-top: 20px;
  overflow: auto;
  max-height: fit-content;
`;

const VolumeInfoTable = styled.table`
  max-width: 600px;
  width: 100%;
  text-align: center;
  border-collapse: collapse;
`;

const VolumeInfoTableRow = styled.tr`
  height: 30px;
  border-bottom: 2px solid #f3f4f6;
`;
const VolumeInfoTableHeader = styled(VolumeInfoTableRow)`
  position: sticky;
  top: 0;
  background: #f3f4f6;
  color: black;
`;
const ContentNumberPerMedia = styled.span`
  padding: 0px 8px;
  white-space: nowrap;
`;

interface VolumePrice2ModelInfoProps {
  volumePrice2Model: Extract<PriceModel, { type: 'VOLUME2' }>;
}
export const VolumePrice2ModelInfo = ({
  volumePrice2Model,
}: VolumePrice2ModelInfoProps) => {
  const windowSize = useWindowSize();
  const isNotMobile = windowSize !== WindowSize.MOBILE;

  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.payment.table.volumePrice2ModelInfo',
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const modalityLabel = useModalityLabel();
  const groupedByVirtualSize = useMemo(
    () =>
      Object.entries(volumePrice2Model.modalities).reduce<
        Record<number, [string, ModalityVirtualVolume][]>
      >((group, [modality, info]) => {
        (group[info.virtualSize] = group[info.virtualSize] ?? []).push([
          modality,
          info,
        ]);
        return group;
      }, {}),
    [volumePrice2Model.modalities],
  );
  const sortedGroup = useMemo(
    () =>
      Object.entries(groupedByVirtualSize).sort(
        (a, b) => Number.parseInt(b[0], 10) - Number.parseInt(a[0], 10),
      ),
    [groupedByVirtualSize],
  );

  return (
    <VolumeInfoContainer>
      {isNotMobile && <Message>{t('message')}</Message>}
      <ClickAwayListener onClickAway={() => setModalOpen(false)}>
        <VolumeInfoIconModalContainer>
          <VolumeInfoIcon
            onClick={() => setModalOpen(prev => !prev)}
            ref={buttonRef}
          />
          <VolumeInfoModal
            open={modalOpen}
            anchorEl={buttonRef.current}
            placement="bottom-end">
            <VolumeInfoHeading>{t('heading1')}</VolumeInfoHeading>
            <TableWrapper>
              <VolumeInfoTable>
                <thead>
                  <VolumeInfoTableHeader>
                    <th>{t('unit')}</th>
                    <th>{t('price')}</th>
                  </VolumeInfoTableHeader>
                </thead>
                <tbody>
                  {volumePrice2Model.units
                    .sort((a, b) => a.volume - b.volume)
                    .map(unit => (
                      <VolumeInfoTableRow key={unit.name}>
                        <td>
                          {unit.name} ({byteToString(unit.volume)})
                        </td>
                        <td>{formatter(unit.price)}</td>
                      </VolumeInfoTableRow>
                    ))}
                </tbody>
              </VolumeInfoTable>
            </TableWrapper>
            {volumePrice2Model.useExpectedPrice !== true && (
              <>
                <Spacer height={8} />
                <VolumeInfoHeading>{t('heading2')}</VolumeInfoHeading>
                <TableWrapper>
                  <VolumeInfoTable>
                    <thead>
                      <VolumeInfoTableHeader>
                        <th>{t('modality')}</th>
                        <th>{t('capacity')}</th>
                      </VolumeInfoTableHeader>
                    </thead>
                    <tbody>
                      {sortedGroup.map(([virtualSize, modalities]) => {
                        const { unit, unitNumber, contentNumber } =
                          calculateMediaUnitCapacity(
                            Number.parseInt(virtualSize, 10),
                            volumePrice2Model.units,
                          );
                        return (
                          <VolumeInfoTableRow key={virtualSize}>
                            <td>
                              {modalities
                                .map(([modality]) => modalityLabel(modality))
                                .join(', ')}
                            </td>
                            <td>
                              <ContentNumberPerMedia>
                                {contentNumber}건 당 {unit.name} {unitNumber}
                                {unit.unit ?? '개'}
                              </ContentNumberPerMedia>
                            </td>
                          </VolumeInfoTableRow>
                        );
                      })}
                    </tbody>
                  </VolumeInfoTable>
                </TableWrapper>
              </>
            )}
          </VolumeInfoModal>
        </VolumeInfoIconModalContainer>
      </ClickAwayListener>
    </VolumeInfoContainer>
  );
};
